// ============================================
// SHARED CONSTANTS, HELPERS & UTILITIES
// ============================================

// Indian States List
export const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
]

// Brand Options
export const BRAND_OPTIONS = [
  { value: "Ultratech", label: "Ultratech" },
  { value: "Shree", label: "Shree" },
  { value: "JSW", label: "JSW" },
  { value: "JKSuper", label: "JKSuper" },
  { value: "ACC", label: "ACC" },
  { value: "Ambuja", label: "Ambuja" },
]

// Cement Site Types
export const CEMENT_SITE_TYPES = [
  { value: "EX", label: "EX" },
  { value: "FOR", label: "FOR" },
]

// Required Documents
export const REQUIRED_DOCS = ["mtc", "statement"]
export const REQUIRED_CODE_DOCS = []

// GIF URL for notifications
export const GIF_URL = "/Images/New notification.gif"

// Initial PO Form State
export const INITIAL_PO_FORM = {
  poNumber: "",
  quantity: "",
  grade: "",
  materialType: "",
  basicPrice: "",
  gstPercentage: "",
  totalPrice: "",
  packaging: "",
  gstNo: "",
  panNo: "",
  paymentTerms: "",
  billingAddress: "",
  siteAddress: { address: "", state: "", pincode: "" },
  contactPerson: "",
  contactMobile: "",
  deliverySchedule: "",
  endUse: "",
  category: "",
  email: "",
  tpcCode: "",
  orderedQuantity: "",
  deliveredQuantity: "0",
  creationDate: "",
  status: "active",
  notifiedExpired: false,
  notifiedExceeded: false,
  notifiedPending: false,
  brand: "",
  code: "",
  companyName: "",
  existingBalance: "",
  paymentAmount: "",
  paymentFileUrl: "",
  paymentMethod: "",
  poFileUrl: "",
  quantityType: "",
  rate: "",
  usingExistingBalance: false,
  utrNumber: "",
  cementSiteType: "",
}

// Initial Code Form State
export const INITIAL_CODE_FORM = {
  codeID: "",
  brand: "",
  depoName: "",
  balance: "",
  cementSiteType: "",
  depoAccount: {
    AccountNumber: "",
    BeneficiaryName: "",
    IFSC: "",
    Branch: "",
  },
  sites: {
    1: { address: "", state: "", pincode: "" },
  },
}

// Initial Validation State
export const INITIAL_VALIDATION = {
  codeID: { isValid: true, message: "" },
  brand: { isValid: true, message: "" },
  depoName: { isValid: true, message: "" },
  depoAccount: { isValid: true, message: "" },
  sites: { isValid: true, message: "" },
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get Indian Standard Time timestamp
 */
export const getIndianTimestamp = () => {
  const now = new Date()
  const istOffset = 5.5 * 60 * 60 * 1000
  const istTime = new Date(now.getTime() + istOffset)
  return istTime.toISOString().replace("T", " ").replace("Z", "")
}

/**
 * Format timestamp for notifications
 */
export const formatTimestamp = (timestamp) => {
  return `${timestamp.getFullYear()}-${String(timestamp.getMonth() + 1).padStart(2, "0")}-${String(timestamp.getDate()).padStart(2, "0")} ${String(timestamp.getHours()).padStart(2, "0")}:${String(timestamp.getMinutes()).padStart(2, "0")}:${String(timestamp.getSeconds()).padStart(2, "0")}.${timestamp.getMilliseconds()}`
}

/**
 * Format document name from camelCase
 */
export const formatDocumentName = (name) => {
  return name.replace(/([A-Z])/g, " $1").trim()
}

/**
 * Validate GST Number (15 characters)
 */
export const validateGST = (gst) => /^[0-9A-Z]{15}$/.test(gst)

/**
 * Validate PAN Number
 */
export const validatePAN = (pan) => /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan)

/**
 * Validate Mobile Number (10 digits)
 */
export const validateMobile = (mobile) => /^\d{10}$/.test(mobile)

/**
 * Validate Pincode (6 digits)
 */
export const validatePincode = (pincode) => /^\d{6}$/.test(pincode)

/**
 * Validate Email
 */
export const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

/**
 * Validate IFSC Code
 */
export const validateIFSC = (ifsc) => /^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)

/**
 * Validate Code ID (uppercase letters and digits only)
 */
export const validateCodeID = (code) => /^[A-Z0-9]+$/.test(code)

/**
 * Get missing documents for a user
 */
export const getMissingDocuments = (userDocuments, mtcFiles, statementFiles) => {
  const globalMissing = []

  if (mtcFiles.length === 0) globalMissing.push("Material Test Certificate (MTC)")
  if (statementFiles.length === 0) globalMissing.push("User Statement")

  const codeWiseMissing = {}
  Object.entries(userDocuments || {}).forEach(([codeId, docs]) => {
    const missing = []
    REQUIRED_CODE_DOCS.forEach((docType) => {
      if (!docs[docType]) {
        missing.push(formatDocumentName(docType))
      }
    })
    if (missing.length > 0) {
      codeWiseMissing[codeId] = missing
    }
  })

  return { globalMissing, codeWiseMissing }
}

/**
 * Validate New PO Form
 */
export const validatePOForm = (form) => {
  const errors = {}

  if (!form.code) errors.code = "Code is required"
  if (!form.grade) errors.grade = "Grade is required"
  if (!form.quantity || form.quantity <= 0) errors.quantity = "Quantity must be positive"
  if (!form.rate || form.rate <= 0) errors.rate = "Rate must be positive"

  if (!form.siteAddress.address || form.siteAddress.address.length < 5)
    errors.address = "Site address must be at least 5 characters"
  if (!form.siteAddress.state) errors.state = "State is required"
  if (!validatePincode(form.siteAddress.pincode)) errors.pincode = "Pincode must be 6 digits"

  if (!form.basicPrice) errors.basicPrice = "Basic Price is required"
  if (!form.gstPercentage || form.gstPercentage < 0 || form.gstPercentage > 100)
    errors.gstPercentage = "GST % must be between 0 and 100"
  if (!form.totalPrice) errors.totalPrice = "Total price is required"

  if (!validateGST(form.gstNo)) errors.gstNo = "Invalid GST number (15 characters)"
  if (!validatePAN(form.panNo)) errors.panNo = "Invalid PAN number"

  if (!form.paymentTerms) errors.paymentTerms = "Payment terms required"
  if (!form.billingAddress) errors.billingAddress = "Billing address required"
  if (!form.contactPerson) errors.contactPerson = "Contact person required"
  if (!validateMobile(form.contactMobile)) errors.contactMobile = "Invalid mobile (10 digits)"
  if (!form.category) errors.category = "Category is required"
  if (!validateEmail(form.email)) errors.email = "Invalid email address"
  if (!form.brand) errors.brand = "Brand is required"
  if (!form.companyName) errors.companyName = "Company name is required"
  if (!form.quantityType) errors.quantityType = "Quantity type required"

  return { isValid: Object.keys(errors).length === 0, errors }
}

/**
 * Validate New Code Form
 */
export const validateCodeForm = (form, depots) => {
  const errors = {}
  let isValid = true

  // Validate codeID
  if (!form.codeID.trim()) {
    errors.codeID = "Code ID is required"
    isValid = false
  } else if (form.codeID.length > 20) {
    errors.codeID = "Code ID must not exceed 20 characters"
    isValid = false
  } else if (!validateCodeID(form.codeID)) {
    errors.codeID = "Code ID must contain only uppercase letters and digits"
    isValid = false
  }

  // Validate brand
  if (!form.brand.trim()) {
    errors.brand = "Brand is required"
    isValid = false
  }

  // Validate depoName
  if (form.brand && depots.filter((d) => d.brandName === form.brand).length > 0 && !form.depoName.trim()) {
    errors.depoName = "Depot Name is required"
    isValid = false
  }

  // Validate depoAccount
  const { depoAccount } = form
  if (!depoAccount.AccountNumber.trim()) {
    errors.accountNumber = "Account Number is required"
    isValid = false
  } else if (depoAccount.AccountNumber.length > 25 || !validateCodeID(depoAccount.AccountNumber)) {
    errors.accountNumber = "Invalid Account Number format"
    isValid = false
  }

  if (!depoAccount.BeneficiaryName.trim()) {
    errors.beneficiaryName = "Beneficiary Name is required"
    isValid = false
  } else if (!/^[A-Za-z\s]+$/.test(depoAccount.BeneficiaryName)) {
    errors.beneficiaryName = "Beneficiary Name must contain only letters"
    isValid = false
  }

  if (!depoAccount.IFSC.trim()) {
    errors.ifsc = "IFSC Code is required"
    isValid = false
  } else if (!validateIFSC(depoAccount.IFSC)) {
    errors.ifsc = "Invalid IFSC format (AAAA0XXXXXX)"
    isValid = false
  }

  if (!depoAccount.Branch.trim()) {
    errors.branch = "Bank & Branch is required"
    isValid = false
  }

  // Validate sites
  if (!form.sites[1].address || !form.sites[1].state) {
    errors.sites = "Address and state are required"
    isValid = false
  } else if (form.sites[1].pincode && !validatePincode(form.sites[1].pincode)) {
    errors.sites = "Pincode must be 6 digits"
    isValid = false
  }

  return { isValid, errors }
}
